import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  adicionarAncoragem,
  atualizarAncoragem as atualizarAncoragemDominio,
  PONTOS_DE_FIXACAO,
  removerAncoragem as removerAncoragemDominio,
  criarAncoragemAutomatica,
  type AncoragemTabletop,
  type AtualizacoesAncoragem,
  type CenaTabletop,
  type ReferenciaPonto,
  type TipoAncoragem,
} from '../../dominio/tabletop'

/** A ancoragem em desenho: primeiro os pontos de fixação, depois o ponto central. */
export interface DesenhoAncoragem {
  readonly tipo: TipoAncoragem
  readonly fixacoes: readonly ReferenciaPonto[]
  readonly centro: ReferenciaPonto | null
}

const igual = (a: ReferenciaPonto, b: ReferenciaPonto) => a.idObjeto === b.idObjeto && a.idPonto === b.idPonto

/**
 * Estado da ferramenta de ancoragem em V, W ou M: desenhar, selecionar, editar
 * e apagar. Como o desenho da corda, o desenho em andamento fica fora da cena e
 * do histórico: só a ancoragem concluída entra, num passo de desfazer.
 */
export function useAncoragensTabletop({
  cena,
  aplicar,
  gerarId,
  idObjetoSelecionado,
  selecionarObjeto,
  cancelarPosicionamento,
  ativo,
  aoIniciarDesenho,
  aoSelecionar,
}: {
  cena: CenaTabletop
  aplicar: (fn: (cena: CenaTabletop) => CenaTabletop) => void
  gerarId: () => string
  idObjetoSelecionado: string | null
  selecionarObjeto: (id: string | null) => void
  cancelarPosicionamento: () => void
  /** Falso quando a cena está travada. */
  ativo: boolean
  /** Chamado ao começar a desenhar: o chamador cancela o desenho da corda. */
  aoIniciarDesenho: () => void
  /** Chamado ao selecionar uma ancoragem: o chamador tira a seleção da corda. */
  aoSelecionar: () => void
}) {
  const [desenhoAncoragem, setDesenhoAncoragem] = useState<DesenhoAncoragem | null>(null)
  const [idEscolhido, setIdEscolhido] = useState<string | null>(null)

  useEffect(() => {
    if (!ativo) setDesenhoAncoragem(null)
  }, [ativo])

  const selecionarAncoragem = useCallback(
    (id: string | null) => {
      setIdEscolhido(id)
      if (id) {
        selecionarObjeto(null)
        aoSelecionar()
      }
    },
    [aoSelecionar, selecionarObjeto],
  )

  const iniciarAncoragem = useCallback(
    (tipo: TipoAncoragem) => {
      cancelarPosicionamento()
      selecionarObjeto(null)
      setIdEscolhido(null)
      aoIniciarDesenho()
      setDesenhoAncoragem({ tipo, fixacoes: [], centro: null })
    },
    [aoIniciarDesenho, cancelarPosicionamento, selecionarObjeto],
  )

  const cancelarAncoragem = useCallback(() => setDesenhoAncoragem(null), [])

  const removerUltimoPontoDoDesenhoAncoragem = useCallback(
    () =>
      setDesenhoAncoragem((atual) => {
        if (!atual) return atual
        if (atual.centro) return { ...atual, centro: null }
        return { ...atual, fixacoes: atual.fixacoes.slice(0, -1) }
      }),
    [],
  )

  /**
   * Cada toque vai para onde falta: fixações até completar o tipo, depois o
   * ponto central — que conclui a ancoragem. Ponto repetido é ignorado.
   */
  const escolherPontoDaAncoragem = useCallback(
    (referencia: ReferenciaPonto) => {
      if (!desenhoAncoragem) return
      const { tipo, fixacoes, centro } = desenhoAncoragem
      const jaEscolhido = [...fixacoes, ...(centro ? [centro] : [])].some((p) => igual(p, referencia))
      if (jaEscolhido || centro) return

      if (fixacoes.length < PONTOS_DE_FIXACAO[tipo]) {
        setDesenhoAncoragem({ ...desenhoAncoragem, fixacoes: [...fixacoes, referencia] })
        return
      }
      const id = gerarId()
      try {
        // valida antes de mexer no estado: o desenho pode apontar para algo que já saiu da cena
        adicionarAncoragem(cena, { tipo, fixacoes, centro: referencia }, () => id)
      } catch {
        setDesenhoAncoragem(null)
        return
      }
      aplicar((c) => adicionarAncoragem(c, { tipo, fixacoes, centro: referencia }, () => id))
      setDesenhoAncoragem(null)
      selecionarAncoragem(id)
    },
    [aplicar, cena, desenhoAncoragem, gerarId, selecionarAncoragem],
  )

  const concluirComVerticeAutomatico = useCallback(() => {
    if (!desenhoAncoragem || desenhoAncoragem.fixacoes.length < PONTOS_DE_FIXACAO[desenhoAncoragem.tipo]) return
    const { tipo, fixacoes } = desenhoAncoragem
    try {
      const res = criarAncoragemAutomatica(cena, fixacoes, tipo, gerarId)
      aplicar((c) => criarAncoragemAutomatica(c, fixacoes, tipo, gerarId).cena)
      setDesenhoAncoragem(null)
      if (res.idAncoragem) selecionarAncoragem(res.idAncoragem)
    } catch {
      setDesenhoAncoragem(null)
    }
  }, [aplicar, cena, desenhoAncoragem, gerarId, selecionarAncoragem])

  const atualizarAncoragem = useCallback(
    (id: string, alteracoes: AtualizacoesAncoragem) => aplicar((c) => atualizarAncoragemDominio(c, id, alteracoes)),
    [aplicar],
  )
  const removerAncoragem = useCallback(
    (id: string) => {
      aplicar((c) => removerAncoragemDominio(c, id))
      setIdEscolhido((atual) => (atual === id ? null : atual))
    },
    [aplicar],
  )

  const ancoragemSelecionada: AncoragemTabletop | null = useMemo(
    () => (idObjetoSelecionado ? null : (cena.ancoragens?.find((a) => a.id === idEscolhido) ?? null)),
    [cena.ancoragens, idEscolhido, idObjetoSelecionado],
  )

  return {
    desenhoAncoragem,
    ancoragemSelecionada,
    idAncoragemSelecionada: ancoragemSelecionada?.id ?? null,
    iniciarAncoragem,
    cancelarAncoragem,
    removerUltimoPontoDoDesenhoAncoragem,
    escolherPontoDaAncoragem,
    concluirComVerticeAutomatico,
    selecionarAncoragem,
    atualizarAncoragem,
    removerAncoragem,
  }
}
